<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import type { Editor } from '@tiptap/core'
  import { currentViewMode, type EditorViewMode } from '$lib/stores/editorViewStore'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { get } from 'svelte/store'
  import LinkInput from '../ui/LinkInput.svelte'
  import { toggleBlockquote as sharedToggleBlockquote } from '$lib/utils/editorCommands'
  import { toggleHeadingWithSelection } from '$lib/tiptap/shortcutUtils'
  import { ClipboardService } from '$lib/services/ClipboardService'
  
  // Import lucide icons
  import {
    Bold,
    Italic,
    Strikethrough,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    TextQuote,
    Code,
    Code2,
    Link,
    Image,
    ChevronDown,
    Minus,
    FileDown
  } from 'lucide-svelte'

  // Import highlight.js for syntax highlighting
  import hljs from 'highlight.js/lib/core'
  import javascript from 'highlight.js/lib/languages/javascript'
  import typescript from 'highlight.js/lib/languages/typescript'
  import python from 'highlight.js/lib/languages/python'
  import java from 'highlight.js/lib/languages/java'
  import cpp from 'highlight.js/lib/languages/cpp'
  import css from 'highlight.js/lib/languages/css'
  import xml from 'highlight.js/lib/languages/xml' // for HTML
  import json from 'highlight.js/lib/languages/json'
  import markdown from 'highlight.js/lib/languages/markdown'
  import bash from 'highlight.js/lib/languages/bash'

  // Register languages
  hljs.registerLanguage('javascript', javascript)
  hljs.registerLanguage('typescript', typescript)
  hljs.registerLanguage('python', python)
  hljs.registerLanguage('java', java)
  hljs.registerLanguage('cpp', cpp)
  hljs.registerLanguage('css', css)
  hljs.registerLanguage('html', xml)
  hljs.registerLanguage('xml', xml)
  hljs.registerLanguage('json', json)
  hljs.registerLanguage('markdown', markdown)
  hljs.registerLanguage('bash', bash)
  
  interface Props {
    editor: Editor | null
  }
  
  let { editor = null }: Props = $props()
  
  // State
  let isCollapsed = $state(false)
  let viewMode = $state<EditorViewMode>('source')
  let showLinkPopover = $state(false)
  let activeFormats = $state({
    bold: false,
    italic: false,
    strike: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    link: false,
    heading1: false,
    heading2: false,
    heading3: false
  })
  
  // Load collapsed state and bubble menu preference from localStorage
  onMount(() => {
    const savedState = localStorage.getItem('reading-toolbar-visible')
    isCollapsed = savedState === 'false'
  })
  
  // Subscribe to view mode changes
  let unsubscribeViewMode: (() => void) | null = null
  
  $effect(() => {
    unsubscribeViewMode = currentViewMode.subscribe(mode => {
      viewMode = mode
    })
    
    return () => {
      if (unsubscribeViewMode) unsubscribeViewMode()
    }
  })
  
  // Update active formats function - defined outside of $effect to avoid state mutation issues
  const updateActiveFormats = () => {
    // Strong null check - editor might be null during view mode transitions
    if (!editor || editor === null || editor.isDestroyed) return

    // Defer state update to avoid mutation during render
    queueMicrotask(() => {
      // Double-check editor is still valid in microtask
      if (!editor || editor === null || editor.isDestroyed) return

      try {
        activeFormats = {
          bold: editor.isActive('bold'),
          italic: editor.isActive('italic'),
          strike: editor.isActive('strike'),
          code: editor.isActive('code'),
          codeBlock: editor.isActive('codeBlock'),
          blockquote: editor.isActive('blockquote'),
          bulletList: editor.isActive('bulletList'),
          orderedList: editor.isActive('orderedList'),
          link: editor.isActive('link'),
          heading1: editor.isActive('heading', { level: 1 }),
          heading2: editor.isActive('heading', { level: 2 }),
          heading3: editor.isActive('heading', { level: 3 })
        }
      } catch (error) {
        // Silently catch any errors during view mode transitions
        console.debug('[ReadingModeToolbar] Format update skipped during transition')
      }
    })
  }
  
  // Set up editor event listeners
  $effect(() => {
    if (!editor || editor === null || editor.isDestroyed) return

    // Initial update - defer to next tick to avoid state mutation during render
    setTimeout(updateActiveFormats, 0)

    // Listen to editor updates
    editor.on('selectionUpdate', updateActiveFormats)
    editor.on('transaction', updateActiveFormats)

    return () => {
      // Clean up event listeners when editor changes or component unmounts
      if (editor && editor !== null && !editor.isDestroyed) {
        try {
          editor.off('selectionUpdate', updateActiveFormats)
          editor.off('transaction', updateActiveFormats)
        } catch (error) {
          // Ignore errors during cleanup - editor might already be destroyed
          console.debug('[ReadingModeToolbar] Cleanup during editor transition')
        }
      }
    }
  })
  
  // Listen for shortcut events emitted by the editor
  $effect(() => {
    if (!editor || editor === null || editor.isDestroyed || viewMode !== 'wysiwyg') return

    const handleLinkShortcut = () => {
      if (editor && editor !== null && !editor.isDestroyed) {
        toggleLink()
      }
    }

    const handleCopyHTMLShortcut = () => {
      if (editor && editor !== null && !editor.isDestroyed) {
        copyAsHTML()
      }
    }

    window.addEventListener('readingMode:toggle-link', handleLinkShortcut)
    window.addEventListener('readingMode:copy-html', handleCopyHTMLShortcut)

    return () => {
      window.removeEventListener('readingMode:toggle-link', handleLinkShortcut)
      window.removeEventListener('readingMode:copy-html', handleCopyHTMLShortcut)
    }
  })
  
  // Toggle collapsed state
  function toggleCollapsed() {
    isCollapsed = !isCollapsed
    localStorage.setItem('reading-toolbar-visible', isCollapsed ? 'false' : 'true')
  }
  
  // Command handlers with null checks
  function toggleBold() {
    if (!editor || editor.isDestroyed) return
    editor.chain().focus().toggleBold().run()
  }
  
  function toggleItalic() {
    if (!editor || editor.isDestroyed) return
    editor.chain().focus().toggleItalic().run()
  }
  
  function toggleStrike() {
    if (!editor || editor.isDestroyed) return
    editor.chain().focus().toggleStrike().run()
  }
  
  function toggleHeading(level: 1 | 2 | 3) {
    toggleHeadingWithSelection(editor, level)
  }
  
  function toggleBulletList() {
    if (!editor || editor.isDestroyed) return
    
    const { from, to, empty } = editor.state.selection
    
    if (!empty) {
      // Get selected text
      const selectedText = editor.state.doc.textBetween(from, to)
      
      // Check if selection spans multiple blocks
      const startPos = editor.state.doc.resolve(from)
      const endPos = editor.state.doc.resolve(to)
      
      if (startPos.parent === endPos.parent) {
        // Selection is within a single block
        // Split and create a list with selected text with spacing
        editor.chain()
          .focus()
          .deleteRange({ from, to })
          .insertContent([
            { type: 'paragraph' },
            { 
              type: 'bulletList', 
              content: [{ 
                type: 'listItem', 
                content: [{ 
                  type: 'paragraph', 
                  content: [{ type: 'text', text: selectedText }] 
                }] 
              }] 
            },
            { type: 'paragraph' }
          ])
          .run()
      } else {
        // Selection spans multiple blocks
        editor.chain()
          .focus()
          .setTextSelection({ from, to })
          .toggleBulletList()
          .run()
      }
    } else {
      // No selection, toggle current line
      editor.chain().focus().toggleBulletList().run()
    }
  }
  
  function toggleOrderedList() {
    if (!editor || editor.isDestroyed) return
    
    const { from, to, empty } = editor.state.selection
    
    if (!empty) {
      // Get selected text
      const selectedText = editor.state.doc.textBetween(from, to)
      
      // Check if selection spans multiple blocks
      const startPos = editor.state.doc.resolve(from)
      const endPos = editor.state.doc.resolve(to)
      
      if (startPos.parent === endPos.parent) {
        // Selection is within a single block
        // Split and create a list with selected text with spacing
        editor.chain()
          .focus()
          .deleteRange({ from, to })
          .insertContent([
            { type: 'paragraph' },
            { 
              type: 'orderedList', 
              content: [{ 
                type: 'listItem', 
                content: [{ 
                  type: 'paragraph', 
                  content: [{ type: 'text', text: selectedText }] 
                }] 
              }] 
            },
            { type: 'paragraph' }
          ])
          .run()
      } else {
        // Selection spans multiple blocks
        editor.chain()
          .focus()
          .setTextSelection({ from, to })
          .toggleOrderedList()
          .run()
      }
    } else {
      // No selection, toggle current line
      editor.chain().focus().toggleOrderedList().run()
    }
  }
  
  function toggleBlockquote() {
    sharedToggleBlockquote(editor)
  }
  
  function toggleCode() {
    if (!editor || editor.isDestroyed) return
    editor.chain().focus().toggleCode().run()
  }
  
  function toggleCodeBlock() {
    if (!editor || editor.isDestroyed) return
    
    const { from, to, empty } = editor.state.selection
    
    if (!empty) {
      // Get selected text
      const selectedText = editor.state.doc.textBetween(from, to)
      
      // Check if selection spans multiple blocks
      const startPos = editor.state.doc.resolve(from)
      const endPos = editor.state.doc.resolve(to)
      
      if (startPos.parent === endPos.parent) {
        // Selection is within a single block
        // Split and create a code block with selected text with spacing
        editor.chain()
          .focus()
          .deleteRange({ from, to })
          .insertContent([
            { type: 'paragraph' },
            { type: 'codeBlock', content: [{ type: 'text', text: selectedText }] },
            { type: 'paragraph' }
          ])
          .run()
      } else {
        // Selection spans multiple blocks
        editor.chain()
          .focus()
          .setTextSelection({ from, to })
          .toggleCodeBlock()
          .run()
      }
    } else {
      // No selection, toggle current block
      editor.chain().focus().toggleCodeBlock().run()
    }
  }
  
  function insertHorizontalRule() {
    if (!editor || editor.isDestroyed) return
    editor.chain().focus().setHorizontalRule().run()
  }
  
  function toggleLink() {
    if (!editor || editor.isDestroyed) return
    
    const { from, to } = editor.state.selection
    
    // Check if we have a selection or cursor is in a link
    if (from === to && !editor.isActive('link')) {
      // No selection and not in a link - can't add link
      return
    }
    
    // Show link input dialog
    showLinkPopover = true
  }
  
  async function insertImage(event: MouseEvent) {
    if (!editor || editor.isDestroyed) return

    // Prevent default
    event.preventDefault()

    // Check if we're in demo mode
    const urlParams = new URLSearchParams(window.location.search)
    const isDemoMode = urlParams.get('demo') === 'true'

    if (isDemoMode) {
      // Show notification in demo mode
      import('$lib/stores/toast').then(({ toast }) => {
        toast.warning('데모 모드에서는 이미지 업로드가 지원되지 않습니다. 미리 준비된 샘플 이미지를 사용해보세요.', 5000)
      })
      return
    }

    // File upload only
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('파일 크기는 5MB 이하여야 합니다.')
        return
      }
      
      try {
        // Read file as base64
        const reader = new FileReader()
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = (event) => resolve(event.target?.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        // Check if we're in Electron environment
        if ((window as any).electronAPI?.saveImage) {
          try {
            // Save to attachments folder via IPC
            const result = await (window as any).electronAPI.saveImage({
              base64,
              fileName: file.name
            })
            
            if (result?.success && result.path) {
              // Use relative path like Obsidian (e.g., attachments/image.png)
              // This makes the markdown more portable
              const relativePath = result.path
              
              // Insert using relative path - the renderer will handle converting to absolute path
              editor.chain().focus().setImage({ src: relativePath }).run()
              
              // Dispatch event to refresh file explorer
              window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
              return
            } else if (result?.success && !result.path) {
              // Fallback to base64 if no path is available
              editor.chain().focus().setImage({ src: base64 }).run()
              return
            }
          } catch (ipcError) {
            // Fallback to base64 if IPC fails
          }
        }

        // Fallback: Use base64 (for web or if IPC fails)
        editor.chain().focus().setImage({ src: base64 }).run()
      } catch (error) {
        console.error('Failed to process image:', error)
        alert('이미지 처리 중 오류가 발생했습니다.')
      }
    }
    input.click()
  }
  
  function closeLinkPopover() {
    showLinkPopover = false
  }

  function setHeading(level: 1 | 2 | 3) {
    toggleHeading(level)
  }

  // Copy HTML to clipboard with syntax highlighting (rich format)
  export async function copyAsHTML() {
    if (!editor || editor.isDestroyed) return

    try {
      // Check if there's a selection
      const { from, to } = editor.state.selection
      const hasSelection = from !== to

      // Get HTML from TipTap (selection or full content)
      let rawHTML: string
      let plainText: string

      if (hasSelection) {
        // Get selected content as HTML using DOMSerializer
        const slice = editor.state.doc.slice(from, to)
        plainText = editor.state.doc.textBetween(from, to)

        // Import DOMSerializer from prosemirror-model
        const { DOMSerializer } = await import('@tiptap/pm/model')

        // Use TipTap's schema to serialize
        const serializer = DOMSerializer.fromSchema(editor.schema)
        const fragment = serializer.serializeFragment(slice.content)

        const tempDiv = document.createElement('div')
        tempDiv.appendChild(fragment)
        rawHTML = tempDiv.innerHTML
      } else {
        // No selection - get full content
        rawHTML = editor.getHTML()
        plainText = editor.getText()
      }

      // Apply syntax highlighting to code blocks
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = rawHTML

      // Process all code blocks
      const codeBlocks = tempDiv.querySelectorAll('pre code')
      codeBlocks.forEach((block) => {
        const codeElement = block as HTMLElement
        const language = codeElement.className.match(/language-(\w+)/)?.[1] || 'plaintext'

        try {
          if (language !== 'plaintext' && hljs.getLanguage(language)) {
            const highlighted = hljs.highlight(codeElement.textContent || '', { language })
            codeElement.innerHTML = highlighted.value
            codeElement.className = `hljs language-${language}`
          } else {
            codeElement.className = 'hljs'
          }
        } catch (error) {
          console.warn(`Failed to highlight ${language}:`, error)
          codeElement.className = 'hljs'
        }
      })

      // Get the styled HTML
      const styledHTML = tempDiv.innerHTML

      // Copy as rich format (HTML + plain text)
      const success = await ClipboardService.writeHTML(styledHTML, plainText)

      if (success) {
        // Show success message
        import('$lib/stores/toast').then(({ toast }) => {
          toast.success(hasSelection ? '선택 영역을 복사했습니다' : 'HTML이 클립보드에 복사되었습니다')
        })
      } else {
        import('$lib/stores/toast').then(({ toast }) => {
          toast.error('HTML 복사에 실패했습니다')
        })
      }
    } catch (error) {
      console.error('Failed to copy HTML:', error)
      import('$lib/stores/toast').then(({ toast }) => {
        toast.error('HTML 복사에 실패했습니다')
      })
    }
  }

  // Generate complete HTML document with embedded styles
  function generateHTMLDocument(bodyContent: string, title: string): string {
    return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    /* Base styles */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 16px;
      line-height: 1.6;
      color: #1f2937;
      background: #ffffff;
      padding: 2rem;
      max-width: 800px;
      margin: 0 auto;
    }

    /* Typography */
    h1, h2, h3, h4, h5, h6 {
      font-weight: 700;
      line-height: 1.3;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
      color: #111827;
    }

    h1 { font-size: 2em; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.3em; }
    h2 { font-size: 1.5em; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.3em; }
    h3 { font-size: 1.25em; }
    h4 { font-size: 1.1em; }
    h5, h6 { font-size: 1em; }

    p {
      margin: 1em 0;
    }

    /* Links */
    a {
      color: #3b82f6;
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.2s;
    }

    a:hover {
      border-bottom-color: #3b82f6;
    }

    /* Lists */
    ul, ol {
      margin: 1em 0;
      padding-left: 2em;
    }

    li {
      margin: 0.25em 0;
    }

    /* Blockquotes */
    blockquote {
      margin: 1.5em 0;
      padding: 0.5em 1em;
      border-left: 4px solid #3b82f6;
      background: #f3f4f6;
      color: #4b5563;
      font-style: italic;
    }

    /* Code */
    code {
      font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace;
      font-size: 0.9em;
      background: #f3f4f6;
      padding: 0.2em 0.4em;
      border-radius: 3px;
      color: #e53e3e;
    }

    pre {
      margin: 1.5em 0;
      padding: 1em;
      background: #1f2937;
      border-radius: 6px;
      overflow-x: auto;
    }

    pre code {
      background: transparent;
      color: #e5e7eb;
      padding: 0;
      font-size: 0.875em;
      line-height: 1.5;
    }

    /* Highlight.js theme (GitHub Dark) */
    .hljs {
      display: block;
      overflow-x: auto;
      color: #e5e7eb;
    }

    .hljs-comment,
    .hljs-quote {
      color: #9ca3af;
      font-style: italic;
    }

    .hljs-keyword,
    .hljs-selector-tag,
    .hljs-subst {
      color: #f472b6;
    }

    .hljs-number,
    .hljs-literal,
    .hljs-variable,
    .hljs-template-variable {
      color: #fb923c;
    }

    .hljs-string,
    .hljs-doctag {
      color: #a3e635;
    }

    .hljs-title,
    .hljs-section,
    .hljs-selector-id {
      color: #60a5fa;
      font-weight: bold;
    }

    .hljs-type,
    .hljs-class .hljs-title {
      color: #fbbf24;
    }

    .hljs-tag,
    .hljs-name,
    .hljs-attribute {
      color: #34d399;
    }

    .hljs-regexp,
    .hljs-link {
      color: #c084fc;
    }

    .hljs-symbol,
    .hljs-bullet {
      color: #f472b6;
    }

    .hljs-built_in,
    .hljs-builtin-name {
      color: #60a5fa;
    }

    .hljs-meta {
      color: #6b7280;
    }

    .hljs-deletion {
      background: #fee;
    }

    .hljs-addition {
      background: #efe;
    }

    .hljs-emphasis {
      font-style: italic;
    }

    .hljs-strong {
      font-weight: bold;
    }

    /* Images */
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 1em 0;
    }

    /* Horizontal rule */
    hr {
      margin: 2em 0;
      border: none;
      border-top: 2px solid #e5e7eb;
    }

    /* Tables */
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 1.5em 0;
    }

    th, td {
      border: 1px solid #e5e7eb;
      padding: 0.5em;
      text-align: left;
    }

    th {
      background: #f3f4f6;
      font-weight: 600;
    }

    /* Print styles */
    @media print {
      body {
        padding: 0;
        max-width: none;
      }
    }
  </style>
</head>
<body>
  ${bodyContent}
</body>
</html>`
  }

  // Only show toolbar in WYSIWYG mode when editor is ready
  const isVisible = $derived(viewMode === 'wysiwyg' && editor !== null && !editor?.isDestroyed)
</script>

{#if isVisible}
  <div class="reading-mode-toolbar" class:collapsed={isCollapsed}>
    {#if isCollapsed}
      <!-- Collapsed state - show notch -->
      <div 
        class="toolbar-notch" 
        onclick={toggleCollapsed} 
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleCollapsed()}
        role="button" 
        tabindex="0"
      >
        <div class="notch-indicator">
          <ChevronDown size={14} />
        </div>
      </div>
    {:else}
      <!-- Expanded state - show full toolbar -->
      <div class="toolbar-content">
        <div class="toolbar-buttons">
          <!-- Text Formatting -->
          <div class="button-group">
            <button
              class="toolbar-button"
              class:active={activeFormats.bold}
              onclick={toggleBold}
              title="굵게 (⌘B)"
            >
              <Bold size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.italic}
              onclick={toggleItalic}
              title="기울임 (⌘I)"
            >
              <Italic size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.strike}
              onclick={toggleStrike}
              title="취소선 (⌘⇧D)"
            >
              <Strikethrough size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              onclick={insertHorizontalRule}
              title="구분선 (⌘+Shift+-)"
            >
              <Minus size={16} strokeWidth={2} />
            </button>
          </div>
          
          <div class="separator"></div>
          
          <!-- Headings -->
          <div class="button-group">
            <button
              class="toolbar-button"
              class:active={activeFormats.heading1}
              onclick={() => toggleHeading(1)}
              title="제목 1 (⌘⌥1)"
            >
              <Heading1 size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.heading2}
              onclick={() => toggleHeading(2)}
              title="제목 2 (⌘⌥2)"
            >
              <Heading2 size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.heading3}
              onclick={() => toggleHeading(3)}
              title="제목 3 (⌘⌥3)"
            >
              <Heading3 size={16} strokeWidth={2} />
            </button>
          </div>
          
          <div class="separator"></div>
          
          <!-- Lists -->
          <div class="button-group">
            <button
              class="toolbar-button"
              class:active={activeFormats.bulletList}
              onclick={toggleBulletList}
              title="글머리 기호 목록"
            >
              <List size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.orderedList}
              onclick={toggleOrderedList}
              title="번호 매기기 목록"
            >
              <ListOrdered size={16} strokeWidth={2} />
            </button>
          </div>
          
          <div class="separator"></div>
          
          <!-- Quote & Code -->
          <div class="button-group">
            <button
              class="toolbar-button"
              class:active={activeFormats.blockquote}
              onclick={toggleBlockquote}
              title="인용"
            >
              <TextQuote size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.code}
              onclick={toggleCode}
              title="인라인 코드 (⌘⇧E)"
            >
              <Code size={16} strokeWidth={2} />
            </button>
            
            <button
              class="toolbar-button"
              class:active={activeFormats.codeBlock}
              onclick={toggleCodeBlock}
              title="코드 블록"
            >
              <Code2 size={16} strokeWidth={2} />
            </button>
          </div>
          
          <div class="separator"></div>
          
          <!-- Link & Image -->
          <div class="button-group">
            <button
              class="toolbar-button"
              class:active={activeFormats.link}
              onclick={toggleLink}
              title="링크 (⌘L)"
            >
              <Link size={16} strokeWidth={2} />
            </button>

            <button
              class="toolbar-button"
              onclick={(e) => insertImage(e)}
              title="이미지 삽입"
            >
              <Image size={16} strokeWidth={2} />
            </button>
          </div>

          <div class="separator"></div>

          <!-- Export -->
          <div class="button-group">
            <button
              class="toolbar-button"
              onclick={copyAsHTML}
              title="HTML 복사"
            >
              <FileDown size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
        
        <!-- Collapse button at right end -->
        <button
          class="toolbar-button collapse-button"
          onclick={toggleCollapsed}
          title="Collapse toolbar"
          aria-label="Collapse formatting toolbar"
        >
          <ChevronDown size={16} style="transform: rotate(180deg)" />
        </button>
      </div>
    {/if}
  </div>
{/if}

<!-- Link Input Dialog -->
{#if showLinkPopover && editor}
  <LinkInput
    {editor}
    onClose={closeLinkPopover}
  />
{/if}

<style>
  .reading-mode-toolbar {
    position: relative;
    width: 100%;
    transition: all 0.2s ease;
    z-index: 10;
    margin-bottom: 12px;
  }
  
  .reading-mode-toolbar.collapsed {
    height: 12px;
    display: flex;
    justify-content: center;
  }
  
  /* Notch for collapsed state */
  .toolbar-notch {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    animation: notchFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .toolbar-notch:hover .notch-indicator {
    transform: translateY(1px);
    opacity: 1;
  }
  
  .notch-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 8px;
    background: var(--border-secondary);
    border-radius: 0 0 8px 8px;
    opacity: 0.5;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  :global(.dark-theme) .notch-indicator {
    background: var(--border-secondary);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  .toolbar-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px max(32px, calc(50% - 500px));
    gap: 8px;
    border-bottom: 1px solid var(--border-primary);
    background: var(--background);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    position: relative;
    z-index: 20;
    animation: toolbarSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  :global(.dark-theme) .toolbar-content {
    background: var(--background);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  
  .collapse-button {
    flex-shrink: 0;
    margin-left: auto;
    margin-right: 8px;
    opacity: 0.6;
    transition: all 0.15s ease;
  }
  
  .collapse-button:hover {
    opacity: 1;
    transform: translateY(-1px);
  }
  
  .collapse-button:active {
    transform: scale(0.9);
  }
  
  .toolbar-buttons {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
    flex: 1;
  }
  
  .button-group {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  
  .toolbar-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 4px 6px;
    min-width: 28px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 13px;
    line-height: 1;
    font-family: inherit;
    position: relative;
  }
  
  .toolbar-button::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    background: var(--accent-color);
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
  }
  
  .toolbar-button:hover:not(.active) {
    background: rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
  
  :global(.dark-theme) .toolbar-button:hover:not(.active) {
    background: rgba(255, 255, 255, 0.06);
  }
  
  .toolbar-button:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  .toolbar-button:active:not(:disabled)::before {
    opacity: 0.1;
  }
  
  .toolbar-button.active {
    background: rgba(59, 130, 246, 0.15);
    color: var(--accent-color);
    transform: translateY(0);
    font-weight: 500;
  }
  
  :global(.dark-theme) .toolbar-button.active {
    background: rgba(59, 130, 246, 0.2);
    color: rgb(96, 165, 250);
  }
  
  .toolbar-button.active:hover {
    background: rgba(59, 130, 246, 0.25);
    color: var(--accent-color);
  }
  
  :global(.dark-theme) .toolbar-button.active:hover {
    background: rgba(59, 130, 246, 0.3);
    color: rgb(96, 165, 250);
  }
  
  .toolbar-button.active:active {
    background: rgba(59, 130, 246, 0.3);
    transform: scale(0.95);
  }
  
  :global(.dark-theme) .toolbar-button.active:active {
    background: rgba(59, 130, 246, 0.35);
  }
  
  .toolbar-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  .separator {
    width: 1px;
    height: 16px;
    background: var(--border-secondary);
    opacity: 0.5;
    margin: 0 2px;
  }
  
  /* Animations */
  @keyframes toolbarSlideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes notchFadeIn {
    from {
      transform: translateY(-5px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
